import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import { PensionSchemeService } from '../services';
import * as AT from './actionTypes';
import * as MT from './mutationTypes';
import {
  PensionScheme,
  Response,
  PensionSchemeRequest,
  NewReportingPeriodRequest,
  DeletePensionSchemeRequest,
  PostServiceCostRequest,
  OrgPensionSchemeRequest,
  PaginatedResult,
  DeleteServiceCostRequest,
  UpdateSubFundProportionsRequest
} from '../types';
import { IPensionSchemeState } from './state';
import { SET_LOADER } from '@/modules/organisation/store/mutationTypes';

/**
 * --------------------------------------------------
 *  PensionScheme Actions
 * --------------------------------------------------
 **/
export const PensionSchemeActions: IPensionSchemeActions = {
  [AT.FETCH_ORGANISATION_PENSION_SCHEMES]: (
    { commit },
    orgPensionSchemeRequest: OrgPensionSchemeRequest
  ): Promise<PensionScheme[]> => {
    commit(SET_LOADER, true);
    return PensionSchemeService.getOrganizationPensionSchemes(
      orgPensionSchemeRequest
    ).then((paginatedResult: PaginatedResult) => {
      commit(MT.SET_PAGINATION_META, paginatedResult.meta);
      commit(MT.SET_ORGANISATION_PENSION_SCHEMES, paginatedResult.data);
      commit(SET_LOADER, false);
      return paginatedResult.data;
    });
  },
  [AT.FETCH_FILTER_PENSION_SCHEMES]: (
    { commit },
    orgPensionSchemeRequest: OrgPensionSchemeRequest
  ): Promise<PensionScheme[]> => {
    commit(SET_LOADER, true);
    return PensionSchemeService.getLiteOrganizationPensionSchemes(
      orgPensionSchemeRequest
    ).then((paginatedResult: PaginatedResult) => {
      commit(MT.SET_FILTER_PENSION_SCHEMES, paginatedResult.data);
      commit(SET_LOADER, false);
      return paginatedResult.data;
    });
  },
  [AT.ADD_PENSION_SCHEME]: (
    { commit },
    pensionSchemeRequest
  ): Promise<Response> => {
    return PensionSchemeService.postPensionScheme(pensionSchemeRequest).then(
      (data: Response) => data
    );
  },
  [AT.ADD_NEW_REPORTING_PERIOD]: (
    { commit },
    newReportingPeriod
  ): Promise<Response> => {
    return PensionSchemeService.postNewReportingPeriod(newReportingPeriod).then(
      (data: Response) => data
    );
  },
  [AT.UPDATE_PENSION_SCHEME]: (
    { commit },
    pensionSchemeRequest
  ): Promise<Response> => {
    return PensionSchemeService.putPensionScheme(pensionSchemeRequest).then(
      (data: Response) => data
    );
  },
  [AT.DELETE_PENSION_SCHEME]: (
    { commit },
    deletePensionSchemeRequest
  ): Promise<Response> => {
    return PensionSchemeService.deletePensionScheme(
      deletePensionSchemeRequest
    ).then((data: Response) => data);
  },
  [AT.ADD_SERVICE_COST]: (
    { commit },
    postServiceCostRequest
  ): Promise<Response> => {
    return PensionSchemeService.postServiceCost(postServiceCostRequest).then(
      (data: Response) => data
    );
  },
  [AT.DELETE_SERVICE_COST]: (
    { commit },
    deleteSeriveCostRequest
  ): Promise<Response> => {
    return PensionSchemeService.deleteServiceCost(deleteSeriveCostRequest).then(
      (data: Response) => data
    );
  },
  [AT.UPDATE_SUBFUND_PROPORTIONS]: (
    { commit },
    UpdateSubFundProportionsRequest
  ): Promise<Response> => {
    return PensionSchemeService.updateSubFundProportions(
      UpdateSubFundProportionsRequest
    ).then((data: Response) => data);
  }
};

/**
 * --------------------------------------------------
 *  PensionScheme Action Context Interface
 * --------------------------------------------------
 **/
export interface IPensionSchemeActionContext
  extends ActionContext<IPensionSchemeState, IRootState> {}

/**
 * --------------------------------------------------
 *  PensionScheme Actions Interface
 * --------------------------------------------------
 **/
export interface IPensionSchemeActions
  extends ActionTree<IPensionSchemeState, IRootState> {
  [AT.FETCH_ORGANISATION_PENSION_SCHEMES](
    context: IPensionSchemeActionContext,
    orgPensionSchemeRequest: OrgPensionSchemeRequest
  ): Promise<PensionScheme[]>;
  [AT.ADD_PENSION_SCHEME](
    context: IPensionSchemeActionContext,
    pensionSchemeRequest: PensionSchemeRequest
  ): Promise<Response>;
  [AT.ADD_NEW_REPORTING_PERIOD](
    context: IPensionSchemeActionContext,
    newReportingPeriodRequest: NewReportingPeriodRequest
  ): Promise<Response>;
  [AT.UPDATE_PENSION_SCHEME](
    context: IPensionSchemeActionContext,
    pensionSchemeRequest: PensionSchemeRequest
  ): Promise<Response>;
  [AT.DELETE_PENSION_SCHEME](
    context: IPensionSchemeActionContext,
    deletePensionSchemeRequest: DeletePensionSchemeRequest
  ): Promise<Response>;
  [AT.ADD_SERVICE_COST](
    context: IPensionSchemeActionContext,
    postServiceCostRequest: PostServiceCostRequest
  ): Promise<Response>;
  [AT.DELETE_SERVICE_COST](
    context: IPensionSchemeActionContext,
    deleteServiceCostRequest: DeleteServiceCostRequest
  ): Promise<Response>;
  [AT.UPDATE_SUBFUND_PROPORTIONS](
    context: IPensionSchemeActionContext,
    updateSubFundProportionsRequest: UpdateSubFundProportionsRequest
  ): Promise<Response>;
}
